import type { ColDef, GridReadyEvent } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { ChevronLeft, ChevronRight, RotateCcw } from 'lucide-react';
import React, { useMemo, useRef, useState } from 'react';
import type { LogStatusSummary } from '../services/timeEntryService';
import {
  agGridContainerStyles,
  agGridCSSClasses,
  agGridDefaultColDef,
  agGridDefaultOptions,
  agGridTheme,
  agGridThemeStyles,
} from '../theme/agGridTheme';
import { colors } from '../theme/colors';
import { getCalendarWeek } from '../utils/dateUtils';

interface LogStatusGridProps {
  logStatusData: LogStatusSummary[];
  isFetching?: boolean;
  onReOpen?: (employeeId: string, calendarWeek: string) => void;
  selectedWeek?: string;
  onWeekChange?: (week: string | undefined) => void;
}

// Set filters start with nothing selected so users can search for a specific
// value directly instead of deselecting everything first. With no selection the
// filter is inactive, so all rows remain visible.
const gridDefaultColDef = {
  ...agGridDefaultColDef,
  filterParams: { defaultToNothingSelected: true },
};

const getFirstMondayOfWeek = (weekStr: string): Date | null => {
  const match = weekStr.match(/(\d{4})\s+CW(\d{2})/);
  if (!match) return null;

  const year = parseInt(match[1], 10);
  const week = parseInt(match[2], 10);

  const jan4 = new Date(year, 0, 4);
  const dayOfWeek = jan4.getDay() || 7;
  const firstMondayOfYear = new Date(jan4);
  firstMondayOfYear.setDate(jan4.getDate() - (dayOfWeek - 1));

  const weekOffset = (week - 1) * 7;
  const firstMondayOfWeek = new Date(firstMondayOfYear);
  firstMondayOfWeek.setDate(firstMondayOfYear.getDate() + weekOffset);

  return firstMondayOfWeek;
};

const getPreviousWeek = (weekStr: string): string => {
  const monday = getFirstMondayOfWeek(weekStr);
  if (!monday) return weekStr;

  const previousMonday = new Date(monday);
  previousMonday.setDate(monday.getDate() - 7);
  return getCalendarWeek(previousMonday);
};

const getNextWeek = (weekStr: string): string => {
  const monday = getFirstMondayOfWeek(weekStr);
  if (!monday) return weekStr;

  const nextMonday = new Date(monday);
  nextMonday.setDate(monday.getDate() + 7);
  return getCalendarWeek(nextMonday);
};

export const LogStatusGrid: React.FC<LogStatusGridProps> = ({
  logStatusData,
  isFetching = false,
  onReOpen,
  selectedWeek: selectedWeekProp,
  onWeekChange,
}) => {
  const gridRef = useRef<AgGridReact>(null);
  const currentWeek = useMemo(() => getCalendarWeek(new Date()), []);
  const [internalSelectedWeek, setInternalSelectedWeek] = useState<string>(currentWeek);

  const selectedWeek = selectedWeekProp ?? internalSelectedWeek;

  const filteredData = useMemo(() => {
    return logStatusData.filter((item) => item.calendar_week === selectedWeek);
  }, [logStatusData, selectedWeek]);

  const updateSelectedWeek = (week: string) => {
    if (onWeekChange) {
      onWeekChange(week);
    } else {
      setInternalSelectedWeek(week);
    }
  };

  const handlePreviousWeek = () => {
    updateSelectedWeek(getPreviousWeek(selectedWeek));
  };

  const handleNextWeek = () => {
    updateSelectedWeek(getNextWeek(selectedWeek));
  };

  const ActionCellRenderer = (params: any) => {
    const { data } = params;
    const statusValue = data?.status?.toLowerCase() || '';
    const isClosed = statusValue === 'closed' || statusValue === 'close';
    const isEnabled = isClosed && onReOpen;

    const buttonStyle: React.CSSProperties = {
      backgroundColor: isEnabled ? colors.accent.blue : colors.neutral.grey[300],
      color: colors.neutral.white,
      border: 'none',
      borderRadius: '6px',
      padding: '0.375rem 0.75rem',
      fontSize: '0.75rem',
      fontWeight: '600',
      cursor: isEnabled ? 'pointer' : 'not-allowed',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem',
      minWidth: '80px',
      justifyContent: 'center',
      opacity: isEnabled ? 1 : 0.6,
      maxWidth: '90px',
      height: '30px',
    };

    return (
      <div
        style={{
          display: 'flex',
          gap: '0.5rem',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '0.5rem',

        }}
      >
        {onReOpen && (
          <button
            onClick={() => {
              if (isEnabled) {
                onReOpen(data.employee_id, data.calendar_week);
              }
            }}
            disabled={!isEnabled}
            style={buttonStyle}
            title={isEnabled ? 'Re-open week entries' : 'Only closed weeks can be re-opened'}
          >
            <RotateCcw size={12} />
            Re-open
          </button>
        )}
      </div>
    );
  };

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: 'Employee ID',
        field: 'employee_id',
        width: 180,
        minWidth: 150,
        maxWidth: 200,
        pinned: 'left',
        lockPosition: 'left',
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        sortable: true,
        filter: true,
      },
      {
        headerName: 'Employee Name',
        field: 'employee_name',
        flex: 1,
        minWidth: 200,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        sortable: true,
        filter: true,
        cellRenderer: (params: any) => {
          return (
            <div style={{ fontWeight: '600', color: colors.secondary.text }}>{params.value}</div>
          );
        },
      },
      {
        headerName: 'Calendar Week',
        field: 'calendar_week',
        width: 150,
        minWidth: 120,
        maxWidth: 200,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        sortable: true,
        filter: true,

      },
      {
        headerName: 'Difference Actual vs Project',
        field: 'difference',
        width: 220,
        minWidth: 200,
        maxWidth: 280,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        sortable: true,
        filter: true,
        cellStyle: { overflow: 'hidden' },
        cellRenderer: (params: any) => {
          const value = params.value ? 'Yes' : 'No';
          return (
            <div
              style={{
                width: 'calc(100% - 0.5rem)',
                height: 'calc(100% - 0.5rem)',
                margin: '0.25rem',
                padding: '0.25rem 0.5rem',
                backgroundColor: params.value ? colors.accent.yellow + '20' : colors.accent.green + '20',
                borderRadius: '4px',
                fontSize: '0.875rem',
                fontWeight: '600',
                color: params.value ? colors.accent.yellow : colors.accent.green,
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxSizing: 'border-box',
              }}
            >
              {value}
            </div>
          );
        },
      },
      {
        headerName: 'Status',
        field: 'status',
        width: 120,
        minWidth: 100,
        maxWidth: 150,
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
        sortable: true,
        filter: true,
        cellStyle: { overflow: 'hidden' },
        cellRenderer: (params: any) => {
          const statusValue = params.value?.toLowerCase() || '';
          const isClosed = statusValue === 'closed' || statusValue === 'close';
          const displayStatus = statusValue === 'close' ? 'Closed' : statusValue === 'open' ? 'Open' : params.value;
          return (
            <div
              style={{
                padding: '0.25rem 0.5rem',
                backgroundColor: isClosed ? colors.accent.green + '20' : colors.accent.yellow + '20',
                borderRadius: '4px',
                fontSize: '0.875rem',
                fontWeight: '600',
                color: isClosed ? colors.accent.green : colors.accent.yellow,
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxSizing: 'border-box',
              }}
            >
              {displayStatus}
            </div>
          );
        },
      },
      {
        headerName: 'Actions',
        field: 'actions',
        width: 150,
        minWidth: 120,
        maxWidth: 180,
        pinned: 'right',
        lockPosition: 'right',
        cellRenderer: ActionCellRenderer,
        cellClass: agGridCSSClasses.actionCell,
        headerClass: agGridCSSClasses.modernHeader,
        sortable: false,
        filter: false,
        editable: false,
        suppressHeaderMenuButton: true,
      },
    ],
    [onReOpen]
  );

  const onGridReady = (_params: GridReadyEvent) => {
    // Grid ready handler
  };

  const buttonStyle: React.CSSProperties = {
    padding: '0.5rem',
    backgroundColor: colors.neutral.white,
    border: `1px solid ${colors.neutral.grey[300]}`,
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    color: colors.secondary.text,
  };

  const weekDisplayStyle: React.CSSProperties = {
    padding: '0.5rem 1rem',
    fontSize: '0.875rem',
    fontWeight: '600',
    color: colors.secondary.text,
    backgroundColor: colors.neutral.white,
    border: `1px solid ${colors.neutral.grey[300]}`,
    borderRadius: '6px',
    minWidth: '140px',
    textAlign: 'center',
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          marginBottom: '1rem',
          gap: '0.5rem',
        }}
      >
        <label
          style={{
            fontSize: '0.875rem',
            fontWeight: '500',
            color: colors.secondary.text,
          }}
        >
          Calendar Week:
        </label>
        <button
          onClick={handlePreviousWeek}
          style={buttonStyle}
          title="Previous week"
        >
          <ChevronLeft size={18} />
        </button>
        <div style={weekDisplayStyle}>{selectedWeek}</div>
        <button
          onClick={handleNextWeek}
          style={buttonStyle}
          title="Next week"
        >
          <ChevronRight size={18} />
        </button>
      </div>
      <div style={{ height: 'calc(100dvh - 340px)' }}>
        <style>{agGridThemeStyles}</style>
        <AgGridReact
          ref={gridRef}
          rowData={filteredData}
          columnDefs={columnDefs}
          defaultColDef={gridDefaultColDef}
          gridOptions={{
            ...agGridDefaultOptions,
            theme: agGridTheme,
            onGridReady,
            loading: isFetching,
            suppressNoRowsOverlay: false,
            noRowsOverlayComponentParams: {
              message: 'No log status data available',
            },
            rowNumbers: { width: 40, minWidth: 35 },
          }}
          animateRows={true}
          pagination={true}
          paginationPageSize={50}
          paginationPageSizeSelector={[25, 50, 100, 200]}
        />
      </div>
    </div>
  );
};

